import {
  DotsVerticalIcon,
  Edit01Icon,
  Globe01Icon,
  Lock01Icon,
  Pencil01Icon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';
import { Chip, IconButton as MuiIconButton, Stack } from '@mui/joy';
import Box from '@mui/joy/Box';

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { PopoverDropdownMenu } from '@/components/PopoverDropdownMenu';
import { DeletePromptModal } from '@/components/Prompts/PromptsTable/DeletePromptModal';
import { IconButton, Tooltip } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import {
  useOrganizationId,
  useRepoCommitTags,
  useUpdateRepoMutation,
} from '@/hooks/useSwr';
import { ListCommitsResponse } from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';

import { PageTitle } from '../../components/PageTitle';
import { SetHandleModal } from '../Hub/SetHandle';
import { getPromptDisplayNameFromRepoObj } from '../Prompts/utils/Prompts.utils';
import CommitTags from './Commits/CommitTags';
import CopyPublicLinkButton from './CopyPublicLinkButton';
import { EditPromptMetadataDialog } from './EditPromptMetadataDialog';
import GoToPlaygroundButton from './GoToPlaygroundButton';
import { HubForkButton } from './HubForkButton';
import LikeButton from './LikeButton';
import MakePublicButton from './MakePublicButton';
import { useRepoData } from './hooks/useRepoData';

const RepoHeader = ({
  inHub,
  activeTab,
  commitsWithLookups,
  showProxyProviderWarning,
  setShowProxyProviderWarningModal,
}: {
  inHub: boolean;
  activeTab: number;
  commitsWithLookups?: ListCommitsResponse;
  showProxyProviderWarning?: boolean;
  setShowProxyProviderWarningModal: (open: boolean) => void;
}) => {
  const {
    owner,
    repo,
    commit,
    selectedTenant,
    repoObj,
    playgroundLinkBase,
    playgroundParams,
    playgroundError,
  } = useRepoData(inHub);

  const currentCommit = commit
    ? commitsWithLookups?.commits.find((c) => c.commit_hash.startsWith(commit))
    : null;

  const { data: commitTags } = useRepoCommitTags(owner, repo!);

  const organizationId = useOrganizationId();
  const { authorize } = usePermissions();

  const { trigger: updateRepo, isMutating } = useUpdateRepoMutation(
    owner,
    repo!
  );

  const upstreamRepoOwner = repoObj?.repo?.upstream_repo_full_name?.includes(
    '/'
  )
    ? repoObj?.repo?.upstream_repo_full_name.split('/')[0]
    : null;

  const [setHandleOpen, setSetHandleOpen] = useState(false);
  const [showDeletePromptModal, setShowDeletePromptModal] = useState(false);

  const navigate = useNavigate();

  const updatePublic = (isPublic: boolean) => {
    if (!selectedTenant?.tenant_handle) {
      setSetHandleOpen(true);
    } else {
      updateRepo({ json: { is_public: isPublic } });
    }
  };

  const repoDisplayName = repoObj?.repo
    ? getPromptDisplayNameFromRepoObj(repoObj?.repo)
    : null;

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="flex items-baseline space-x-2 px-4 pt-6">
        <div className="flex flex-col">
          <PageTitle
            className="py-0"
            contentClassName="flex items-center gap-2 max-w-full"
          >
            <span className="truncate">{repoDisplayName}</span>
            <div className="ml-2 flex items-center gap-2 leading-none">
              {repoObj?.repo?.is_public ? (
                <Chip
                  size="sm"
                  startDecorator={<Globe01Icon className="size-4" />}
                  color={'primary'}
                  variant="soft"
                  sx={{ borderRadius: 4 }}
                >
                  Public{repoObj?.repo?.is_archived ? ' Archive' : ''}
                </Chip>
              ) : (
                <Chip
                  size="sm"
                  startDecorator={<Lock01Icon className="size-4" />}
                  color={'neutral'}
                  variant="soft"
                  sx={{ borderRadius: 4 }}
                >
                  Private{repoObj?.repo?.is_archived ? ' Archive' : ''}
                </Chip>
              )}
              {(!inHub || owner === selectedTenant?.tenant_handle) && (
                <>
                  {authorize('prompts:update') && (
                    <Tooltip title="Edit prompt metadata">
                      <Link
                        to={addQueryParams(
                          `/${
                            inHub
                              ? appHubIndexPath + '/' + owner
                              : appPromptsIndexPath
                          }/${repo}/edit`,
                          { organizationId }
                        )}
                      >
                        <MuiIconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                        >
                          <Edit01Icon className="size-4" />
                        </MuiIconButton>
                      </Link>
                    </Tooltip>
                  )}
                  {authorize('prompts:delete') && (
                    <Tooltip title="Delete prompt">
                      <MuiIconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => setShowDeletePromptModal(true)}
                      >
                        <Trash01Icon className="h-5 w-5" />
                      </MuiIconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </PageTitle>
          {repoObj?.repo?.upstream_repo_full_name && (
            <div className="pt-2">
              Forked from{' '}
              <Link
                to={addQueryParams(
                  `/${
                    upstreamRepoOwner &&
                    (upstreamRepoOwner !== (owner ?? repoObj?.repo?.owner) ||
                      (inHub && repoObj?.repo?.is_public))
                      ? `${appHubIndexPath}/${repoObj.repo.upstream_repo_full_name}`
                      : `${appPromptsIndexPath}/${
                          repoObj.repo.owner
                            ? repoObj.repo.upstream_repo_full_name.split('/')[1]
                            : repoObj.repo.upstream_repo_full_name
                        }`
                  }`,
                  { organizationId }
                )}
                className="text-brand-green-400"
              >
                {repoObj.repo.upstream_repo_full_name}
              </Link>
            </div>
          )}
          {activeTab !== 1 && currentCommit && (
            <div className="mt-2 max-w-[500px]" ref={containerRef}>
              <CommitTags
                tags={
                  commitTags?.filter(
                    (t) => t.commit_id === currentCommit?.id
                  ) ?? []
                }
                containerRef={containerRef}
                commit={currentCommit}
                owner={owner}
                repo={repo}
              />
            </div>
          )}
        </div>
        <Box flexGrow={1} />
        {selectedTenant ? (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent={'flex-end'}
            alignItems={'center'}
          >
            {repoObj?.repo?.is_public && (
              <CopyPublicLinkButton
                owner={repoObj.repo.owner}
                repo={repo}
                commit={commit}
              />
            )}

            <LikeButton />
            {(owner === selectedTenant?.tenant_handle ||
              (!owner && repoObj && authorize('prompts:share'))) && (
              <MakePublicButton
                isPublic={repoObj?.repo?.is_public}
                isMutating={isMutating}
                updatePublic={updatePublic}
                disabled={isMutating || !repoObj?.repo.num_commits}
              />
            )}
            {repoObj && authorize('prompts:create') && (
              <HubForkButton inHub={inHub} />
            )}
            {repoObj?.repo.num_commits ? (
              <GoToPlaygroundButton
                onClick={() => {
                  if (showProxyProviderWarning) {
                    setShowProxyProviderWarningModal(true);
                  } else {
                    navigate(
                      addQueryParams(playgroundLinkBase, playgroundParams)
                    );
                  }
                }}
                error={playgroundError ?? undefined}
                canEdit={repoObj?.repo.tenant_id === selectedTenant.id}
              />
            ) : null}
          </Stack>
        ) : (
          <GoToPlaygroundButton
            onClick={() => {
              navigate(addQueryParams(playgroundLinkBase, { commit }));
            }}
            error={playgroundError ?? undefined}
            canEdit={false}
          />
        )}
      </div>
      <SetHandleModal
        open={setHandleOpen}
        onClose={() => {
          setSetHandleOpen(false);
        }}
        onSuccess={() => {
          updateRepo({ json: { is_public: true } });
        }}
      />
      {repoObj && (
        <DeletePromptModal
          handle={repoObj?.repo.repo_handle}
          isPublic={repoObj?.repo.is_public}
          open={showDeletePromptModal}
          onClose={() => {
            setShowDeletePromptModal(false);
          }}
          onSuccess={() => {
            const link = addQueryParams(appPromptsIndexPath, {
              organizationId,
            });
            navigate(`/${link}`);
          }}
        />
      )}
    </>
  );
};

export const RepoButtons = ({
  inHub,
  showProxyProviderWarning,
  setShowProxyProviderWarningModal,
}: {
  inHub: boolean;

  showProxyProviderWarning?: boolean;
  setShowProxyProviderWarningModal: (open: boolean) => void;
}) => {
  const {
    owner,
    repo,
    commit,
    selectedTenant,
    repoObj,
    playgroundLinkBase,
    playgroundParams,
    playgroundError,
  } = useRepoData(inHub);

  const organizationId = useOrganizationId();

  const { authorize } = usePermissions();

  const [showUpdatePromptModal, setShowUpdatePromptModal] = useState(false);
  const { trigger: updateRepo, isMutating } = useUpdateRepoMutation(
    owner,
    repo!
  );

  const [handleOpen, setHandleOpen] = useState(false);
  const [showDeletePromptModal, setShowDeletePromptModal] = useState(false);
  const navigate = useNavigate();

  const updatePublic = (isPublic: boolean) => {
    if (!selectedTenant?.tenant_handle) {
      setHandleOpen(true);
    } else {
      updateRepo({ json: { is_public: isPublic } });
    }
  };

  const threeDotMenuOptions = [
    authorize('prompts:update') && {
      label: 'Update metadata',
      icon: <Pencil01Icon className="h-4 w-4" />,
      onClick: () => setShowUpdatePromptModal(true),
      dataTestId: 'open-update-prompt-modal',
    },

    authorize('prompts:delete') && {
      label: 'Delete Prompt',
      icon: <Trash01Icon className="h-4 w-4" />,
      onClick: () => setShowDeletePromptModal(true),
      dataTestId: 'open-delete-prompt-modal',
    },
  ].filter(Boolean);

  return (
    <div className="flex items-center gap-2">
      {repoObj?.repo?.is_public && (
        <CopyPublicLinkButton
          owner={repoObj.repo.owner}
          repo={repo}
          commit={commit}
        />
      )}

      {repoObj?.repo?.is_public && <LikeButton />}
      {(owner === selectedTenant?.tenant_handle ||
        (!owner && repoObj && authorize('prompts:share'))) && (
        <MakePublicButton
          isPublic={repoObj?.repo?.is_public}
          isMutating={isMutating}
          updatePublic={updatePublic}
          disabled={isMutating || !repoObj?.repo.num_commits}
        />
      )}
      {repoObj && authorize('prompts:create') && (
        <HubForkButton inHub={inHub} />
      )}
      {repoObj?.repo.num_commits ? (
        <GoToPlaygroundButton
          onClick={() => {
            if (showProxyProviderWarning) {
              setShowProxyProviderWarningModal(true);
            } else {
              navigate(addQueryParams(playgroundLinkBase, playgroundParams));
            }
          }}
          error={playgroundError ?? undefined}
          canEdit={repoObj?.repo.tenant_id === selectedTenant?.id}
        />
      ) : null}
      <PopoverDropdownMenu
        values={
          threeDotMenuOptions as {
            label: string;
            icon: React.ReactNode;
            onClick: () => void;
            dataTestId: string;
          }[]
        }
        onSelect={(option) => option.onClick?.()}
        getTitle={(option) => option.label}
        getIsSelected={() => false}
        getIcon={(option) => option.icon}
        getDataTestId={(option) => option.dataTestId || ''}
        asChild
      >
        <IconButton
          size="md"
          label="More"
          variant="plain"
          color="secondary"
          icon={DotsVerticalIcon}
        />
      </PopoverDropdownMenu>
      <SetHandleModal
        open={handleOpen}
        onClose={() => {
          setHandleOpen(false);
        }}
        onSuccess={() => {
          updateRepo({ json: { is_public: true } });
        }}
      />
      {repoObj && (
        <DeletePromptModal
          handle={repoObj?.repo.repo_handle}
          isPublic={repoObj?.repo.is_public}
          open={showDeletePromptModal}
          onClose={() => {
            setShowDeletePromptModal(false);
          }}
          onSuccess={() => {
            const link = addQueryParams(appPromptsIndexPath, {
              organizationId,
            });
            navigate(`/${link}`);
          }}
        />
      )}
      {repoObj && (
        <EditPromptMetadataDialog
          open={showUpdatePromptModal}
          onClose={() => setShowUpdatePromptModal(false)}
          repo={repoObj.repo}
        />
      )}
    </div>
  );
};

export default RepoHeader;
